/**
 * Authentication Service
 * Handles all authentication-related API calls
 *
 * Uses shared API client with automatic token refresh for session extension
 */

import { AxiosError } from 'axios';
import { apiClient } from './apiClient';
import {
  RegisterRequest,
  LoginRequest,
  UpdateProfileRequest,
  LoginResponse,
  RegisterResponse,
  VerifyEmailResponse,
  ForgotPasswordResponse,
  ResetPasswordResponse,
  ChangePasswordResponse,
  User,
  ApiError,
} from '../types/auth';
import { ApiResponse } from '../types';

// Error handler helper
const handleApiError = (error: AxiosError): ApiError => {
  if (error.response) {
    const data = error.response.data as {
      message?: string;
      error?: string | { code?: string; message?: string };
      details?: Record<string, string[]>
    };

    // Extract error message - handle both string error and nested error object
    let errorMessage = 'An error occurred';
    let errorCode = error.response.status.toString();

    if (data.message) {
      errorMessage = data.message;
    } else if (data.error) {
      if (typeof data.error === 'string') {
        errorMessage = data.error;
      } else if (typeof data.error === 'object' && data.error.message) {
        errorMessage = data.error.message;
        if (data.error.code) {
          errorCode = data.error.code;
        }
      }
    }

    return {
      code: errorCode,
      message: errorMessage,
      details: data.details,
      statusCode: error.response.status,
    };
  }

  if (error.request) {
    return {
      code: 'NETWORK_ERROR',
      message: 'Unable to connect to server. Please check your internet connection.',
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: error.message || 'An unexpected error occurred',
  };
};

// ============================================
// AUTH API METHODS
// ============================================

/**
 * Register a new user
 */
export const register = async (data: RegisterRequest): Promise<RegisterResponse> => {
  try {
    const response = await apiClient.post<ApiResponse<RegisterResponse>>('/auth/register', data);
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

/**
 * Login user
 */
export const login = async (data: LoginRequest): Promise<LoginResponse> => {
  try {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/login', data);
    const { user, tokens } = response.data.data;

    // Store tokens
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    localStorage.setItem('user', JSON.stringify(user));

    // If remember me is checked, extend token expiry (handled by backend)
    if (data.rememberMe) {
      localStorage.setItem('rememberMe', 'true');
    }

    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

/**
 * Logout user
 */
export const logout = async (): Promise<void> => {
  try {
    // Call backend logout if needed
    await apiClient.post('/auth/logout');
  } catch (error) {
    // Continue with local logout even if API call fails
    console.warn('Logout API call failed:', error);
  } finally {
    // Clear local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
  }
};

/**
 * Send forgot password email
 */
export const forgotPassword = async (email: string): Promise<ForgotPasswordResponse> => {
  try {
    const response = await apiClient.post<ApiResponse<ForgotPasswordResponse>>('/auth/forgot-password', { email });
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

/**
 * Reset password with token
 */
export const resetPassword = async (token: string, password: string): Promise<ResetPasswordResponse> => {
  try {
    const response = await apiClient.post<ApiResponse<ResetPasswordResponse>>('/auth/reset-password', {
      token,
      newPassword: password,
    });
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

/**
 * Verify email with token
 */
export const verifyEmail = async (token: string): Promise<VerifyEmailResponse> => {
  try {
    const response = await apiClient.get<ApiResponse<VerifyEmailResponse>>(`/auth/verify-email?token=${token}`);
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

/**
 * Send verification email
 */
export const sendVerificationEmail = async (): Promise<{ message: string }> => {
  try {
    const response = await apiClient.post<ApiResponse<{ message: string }>>('/auth/send-verification-email');
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

/**
 * Send verification email for a specific email (no auth required).
 * Used on the login page when the user hits EMAIL_NOT_VERIFIED.
 */
export const sendVerificationEmailFor = async (email: string): Promise<{ message: string }> => {
  try {
    const response = await apiClient.post<ApiResponse<{ message: string }>>(
      '/auth/send-verification-email',
      { email },
    );
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

/**
 * Change password (authenticated)
 */
export const changePassword = async (
  oldPassword: string,
  newPassword: string
): Promise<ChangePasswordResponse> => {
  try {
    const response = await apiClient.post<ApiResponse<ChangePasswordResponse>>('/auth/change-password', {
      oldPassword,
      newPassword,
    });
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

// ============================================
// USER API METHODS
// ============================================

/**
 * Get current user profile
 */
export const getMe = async (): Promise<User> => {
  try {
    const response = await apiClient.get<ApiResponse<User>>('/users/me');
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

/**
 * Update current user profile
 */
export const updateMe = async (data: UpdateProfileRequest): Promise<User> => {
  try {
    const response = await apiClient.patch<ApiResponse<User>>('/users/me', data);
    const user = response.data.data;

    // Update stored user
    localStorage.setItem('user', JSON.stringify(user));

    return user;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

/**
 * Upload avatar image
 */
export const uploadAvatar = async (file: File): Promise<{ avatarUrl: string; user: User }> => {
  try {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await apiClient.post<ApiResponse<{ avatarUrl: string; user: User }>>(
      '/users/avatar',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    const { user } = response.data.data;

    // Update stored user
    localStorage.setItem('user', JSON.stringify(user));

    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('accessToken');
  return !!token;
};

/**
 * Get stored user from localStorage
 */
export const getStoredUser = (): User | null => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr) as User;
    } catch {
      return null;
    }
  }
  return null;
};

const authService = {
  register,
  login,
  logout,
  forgotPassword,
  resetPassword,
  verifyEmail,
  sendVerificationEmail,
  sendVerificationEmailFor,
  changePassword,
  getMe,
  updateMe,
  uploadAvatar,
  isAuthenticated,
  getStoredUser,
};

export default authService;
